import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Popover, ToggleButtonGroup, ToggleButton, styled } from '@mui/material';
import { AppContext } from '../../context/context';

import { trackAnalyticsEvent } from '../../services/analytics';
import { ANALYTICS_EVENT_NAMES } from '../../services/eventTaxonomy';
import { MAP_UI_CORNER_INSET_PX } from './mapUiLayout';

import { Icon } from '@liftango/ui-icons';
import { ICON_FALLBACKS } from '../../utils/iconFallbacks';
import { opsColors, opsElevation } from '../../theme/opsTokens';
import { IconButton } from '@liftango/ui-elements';
import { useGoogleBasemap } from '../../services/googleTiles/useGoogleBasemap';
import { availableBasemapKeys } from '../../services/googleTiles/basemap';

const StyledToggleButtonGroup = styled(ToggleButtonGroup, {
    shouldForwardProp: (prop) => prop !== 'embedded',
})<{ embedded?: boolean }>(({ embedded }) => ({
    position: embedded ? 'static' : 'absolute',
    bottom: embedded ? 'auto' : MAP_UI_CORNER_INSET_PX,
    right: embedded ? 'auto' : MAP_UI_CORNER_INSET_PX,
    width: embedded ? '100%' : 'auto',
    justifyContent: embedded ? 'center' : 'flex-start',
    backgroundColor: opsColors.neutral.panel,
    borderRadius: 24,
    border: `1px solid ${opsColors.neutral.line}`,
    boxShadow: embedded ? 'none' : opsElevation.drawer.shadow,
    zIndex: 1000,
    '& .MuiToggleButton-root': {
        border: 'none',
        borderRadius: 24,
        padding: embedded ? '8px 12px' : '10px 14px',
        color: opsColors.neutral.inkSoft,
        backgroundColor: opsColors.neutral.panel,
        '&.Mui-selected': {
            backgroundColor: opsColors.brand.primary,
            color: '#ffffff',
            '&:hover': {
                backgroundColor: opsColors.brand.primaryHover,
            },
        },
        '&:hover': {
            backgroundColor: opsColors.neutral.panelAlt,
        },
        '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
        },
    },
}));

interface BasemapSwitcherProps {
    embedded?: boolean;
}

export default function BasemapSwitcher({ embedded = false }: BasemapSwitcherProps) {
    const { t } = useTranslation('map');
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("BasemapSwitcher must be used within the AppContext provider");
    }

    const { mapSettings } = context;
    const { basemap, setBasemap } = mapSettings;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const visibleBasemaps: readonly string[] = availableBasemapKeys(useGoogleBasemap().active);

    const handleChange = (event: React.MouseEvent<HTMLElement>, newBasemap: string | null) => {
        if (newBasemap !== null && newBasemap !== basemap) {
            trackAnalyticsEvent(ANALYTICS_EVENT_NAMES.basemap_changed, {
                new_basemap: newBasemap,
                previous_basemap: basemap,
                timestamp: new Date().toISOString(),
            });
            setBasemap(newBasemap);
        }
    };

    if (embedded) {
        const options = [
            { key: 'standard', label: 'Standard', icon: <Icon size={16} name="map" /> },
            { key: 'aesthetic', label: 'Aesthetic', icon: <Icon size={16} d={ICON_FALLBACKS.AutoAwesome} /> },
            { key: 'satellite', label: 'Satellite', icon: <Icon size={16} d={ICON_FALLBACKS.Satellite} /> },
            { key: 'transit', label: 'Transit', icon: <Icon size={16} name="train" /> },
        ].filter((option) => visibleBasemaps.includes(option.key));

        return (
            <>
                <IconButton
                    variant="ghost"
                    label="Map layers"
                    icon={<Icon size={18} d={ICON_FALLBACKS.Layers} />}
                    onClick={(event) => setAnchorEl(event.currentTarget as HTMLElement)}
                    style={{
                        width: 36,
                        height: 36,
                        minWidth: 36,
                        minHeight: 36,
                        borderRadius: 6,
                        backgroundColor: anchorEl ? opsColors.neutral.panelAlt : 'transparent',
                        border: `1px solid ${anchorEl ? opsColors.neutral.lineStrong : 'transparent'}`,
                        boxShadow: 'none',
                    }}
                />
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={() => setAnchorEl(null)}
                    anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'center', horizontal: 'right' }}
                    slotProps={{
                        paper: {
                            sx: {
                                ml: 1,
                                borderRadius: '2px',
                                border: `1px solid ${opsColors.neutral.line}`,
                                boxShadow: 'none',
                                backgroundColor: opsColors.neutral.panel,
                                p: 0,
                                overflow: 'hidden',
                                minWidth: 180,
                            },
                        },
                    }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        {options.map((option) => {
                            const isSelected = basemap === option.key;
                            return (
                                <Box
                                    key={option.key}
                                    component="button"
                                    type="button"
                                    onClick={(e) => {
                                        e.preventDefault();
                                        handleChange(e as any, option.key);
                                        setAnchorEl(null);
                                    }}
                                    sx={{
                                        appearance: 'none',
                                        borderWidth: 0,
                                        outline: 'none',
                                        cursor: 'pointer',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        justifyContent: 'flex-start',
                                        width: '100%',
                                        height: 30,
                                        px: 0.875,
                                        borderRadius: 0,
                                        backgroundColor: isSelected ? opsColors.brand.primary : opsColors.neutral.panel,
                                        color: isSelected ? '#ffffff' : opsColors.neutral.inkSoft,
                                        borderTop: `1px solid ${opsColors.neutral.line}`,
                                        borderBottom: `1px solid ${opsColors.neutral.line}`,
                                        borderLeft: 'none',
                                        borderRight: 'none',
                                        whiteSpace: 'nowrap',
                                        transition: 'background-color 0.16s ease, color 0.16s ease',
                                        '&:hover': {
                                            backgroundColor: isSelected ? opsColors.brand.primaryHover : opsColors.neutral.panelAlt,
                                        },
                                    }}
                                >
                                    {option.icon}
                                    <Box component="span" sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1 }}>
                                        {option.label}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Popover>
            </>
        );
    }

    return (
        <Box sx={embedded ? { display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'center' } : { position: 'absolute', bottom: 0, right: 0, zIndex: 1000, display: 'flex', alignItems: 'center' }}>
            <StyledToggleButtonGroup embedded={embedded}
                value={basemap}
                exclusive
                onChange={handleChange}
                aria-label={t('controls.basemap.ariaGroup')}
            >
                {[
                    <ToggleButton key="standard" value="standard" aria-label={t('controls.basemap.standard')}>
                        <Icon size={20} name="map" />
                    </ToggleButton>,
                    <ToggleButton key="aesthetic" value="aesthetic" aria-label={t('controls.basemap.aesthetic')}>
                        <Icon size={20} d={ICON_FALLBACKS.AutoAwesome} />
                    </ToggleButton>,
                    <ToggleButton key="satellite" value="satellite" aria-label={t('controls.basemap.satellite')}>
                        <Icon size={20} d={ICON_FALLBACKS.Satellite} />
                    </ToggleButton>,
                    <ToggleButton key="transit" value="transit" aria-label={t('controls.basemap.transit')}>
                        <Icon size={20} name="train" />
                    </ToggleButton>,
                ].filter((button) => visibleBasemaps.includes(String(button.props.value)))}
            </StyledToggleButtonGroup>
        </Box>
    );
} 
